<template>
  <div class="received-order-detail">
    <div class="flex items-center justify-between mb-4">
      <router-link to="/received-orders" class="flex items-center text-primary hover:underline">
        <feather-icon icon="ArrowLeftIcon" svgClasses="w-4 h-4 mr-1" />
        Back to Received Orders
      </router-link>
    </div>

    <div v-if="loading" class="flex justify-center items-center py-10">
      <vs-spinner color="primary" />
    </div>

    <div v-else-if="order" class="received-order-body">
      <!-- Header -->
      <vx-card class="mb-5">
        <div class="flex items-center justify-between">
          <div>
            <!-- <h2 class="font-semibold">Order {{ order.orderId }}</h2> -->
             <div class="flex items-center">
              <h2 class="font-semibold mr-4">
                Order {{ order.orderId }}
              </h2>

              <vs-checkbox
                v-model="govtOrder"
                class="mb-0"
              >
                Govt Order
              </vs-checkbox>
            </div>
            <p class="text-sm text-gray-500">
              {{ typeLabel(order.documentType) }}
              <span v-if="order.orderReferenceId"> · references {{ order.orderReferenceId }}</span>
            </p>
          </div>
          <div class="text-right">
            <vs-chip :color="statusColor(order.status)">{{ order.status }}</vs-chip>
            <vs-chip v-if="order.isCancelled" color="danger" class="ml-1">Cancelled</vs-chip>
          </div>
          <div class="flex-wrap-reverse" v-if="canConvertToInvoice">
            <vs-button
              class="mr-3"
              color="success"
              @click="showConvertDialog()"
              >{{ $t('order.convertToInvoice') }}
            </vs-button>
          </div>
        </div>
      </vx-card>
      <div
        v-if="govtOrder"
        class="vx-row mt-4"
      >
        <div class="vx-col md:w-1/2 w-full">
          <label class="text-sm font-medium">
            Ministry / Statutory Board
          </label>

          <vs-select
            v-model="selectedMinistry"
            class="w-full"
            placeholder="Select Ministry / Statutory Board"
          >
            <vs-select-item
              v-for="ministry in ministryOptions"
              :key="ministry"
              :value="ministry"
              :text="ministry"
            />
          </vs-select>
        </div>

        <div class="vx-col md:w-1/2 w-full">
          <label class="text-sm font-medium">
            Sub-Business Unit
          </label>

          <vs-select
            v-model="selectedSubBusinessUnit"
            class="w-full"
            placeholder="Select Sub-Business Unit"
            :disabled="!selectedMinistry"
          >
            <vs-select-item
              v-for="unit in govtSubBusinessUnitOptions"
              :key="unit.value"
              :value="unit.value"
              :text="unit.text"
            />
          </vs-select>
        </div>
      </div>
      <div class="vx-row">
        <!-- Parties -->
        <div class="vx-col md:w-1/2 w-full mb-5">
          <vx-card title="Buyer (sender)" no-shadow card-border>
            <dl class="detail-list">
              <dt>Name</dt><dd>{{ order.buyerName || '—' }}</dd>
              <dt>UEN</dt><dd>{{ order.buyerUen || '—' }}</dd>
              <dt>Peppol ID</dt><dd>{{ order.senderPid || '—' }}</dd>
              <dt>Endpoint</dt><dd>{{ order.buyerEndpointId || '—' }}</dd>
               <dt>Email</dt><dd>{{ order.buyerEmail || '—' }}</dd>
            </dl>
          </vx-card>
        </div>
        <div class="vx-col md:w-1/2 w-full mb-5">
          <vx-card title="Seller (you)" no-shadow card-border>
            <dl class="detail-list">
              <dt>Name</dt><dd>{{ order.sellerName || '—' }}</dd>
              <dt>UEN</dt><dd>{{ order.sellerUen || '—' }}</dd>
              <dt>Receiver PID</dt><dd>{{ order.receiverPid || '—' }}</dd>
              <dt>Issue Date</dt>
              <dd>{{ order.issueDate ? formatDate(order.issueDate) : '—' }}</dd>
            </dl>
          </vx-card>
        </div>
      </div>

      <!-- Monetary + balance -->
      <vx-card title="Amounts" no-shadow card-border class="mb-5">
        <div class="vx-row">
          <div class="vx-col md:w-1/3 w-full">
            <p class="text-xs text-gray-500">Line Extension</p>
            <p class="font-semibold">{{ formatAmount(order.lineExtensionAmount) }}</p>
          </div>
          <div class="vx-col md:w-1/3 w-full">
            <p class="text-xs text-gray-500">Payable</p>
            <p class="font-semibold">{{ formatAmount(order.payableAmount) }}</p>
          </div>
          <div class="vx-col md:w-1/3 w-full">
            <p class="text-xs text-gray-500">Outstanding Balance</p>
            <p
              class="font-semibold"
              :class="order.outstandingBalance > 0 ? 'text-warning' : 'text-success'"
            >
              {{ order.outstandingBalance != null ? formatAmount(order.outstandingBalance) : '—' }}
            </p>
            <p v-if="order.balanceUpdatedAt" class="text-xs text-gray-400">
              updated {{ formatDateTime(order.balanceUpdatedAt) }}
            </p>
          </div>
        </div>
      </vx-card>

      <!-- Line items -->
      <vx-card title="Order Lines" no-shadow card-border class="mb-5">
        <vs-table v-if="lineItems.length" :data="lineItems" stripe>
          <template slot="thead">
            <vs-th>#</vs-th>
            <vs-th>Item</vs-th>
            <vs-th>Qty</vs-th>
            <vs-th>Outstanding Qty</vs-th>
            <vs-th>Unit</vs-th>
            <vs-th>Unit Price</vs-th>
            <vs-th>Line Amount</vs-th>
          </template>
          <template slot-scope="{ data }">
            <vs-tr :key="i" v-for="(line, i) in data">
              <vs-td>{{ line.id || i + 1 }}</vs-td>
              <vs-td>
                <p class="text-sm font-medium">{{ line.name || '—' }}</p>
                <p v-if="line.description" class="text-xs text-gray-500">{{ line.description }}</p>
                <p v-if="line.lineNote" class="text-xs text-gray-500">Note: {{ line.lineNote }}</p>
                <p v-if="line.deliveryDate" class="text-xs text-gray-500">Delivery Date: {{ line.deliveryDate }}</p>
              </vs-td>
              <vs-td>{{ line.quantity != null ? line.quantity : '—' }}</vs-td>
              <vs-td>{{ line.outstandingQuantity != null ? line.outstandingQuantity : '—' }}</vs-td>
              <vs-td>{{ line.unitCode || '—' }}</vs-td>
              <vs-td>{{ line.unitPrice != null ? formatAmount(line.unitPrice) : '—' }}</vs-td>
              <vs-td>{{ line.lineAmount != null ? formatAmount(line.lineAmount) : '—' }}</vs-td>
            </vs-tr>
          </template>
        </vs-table>
        <p v-else class="text-sm text-gray-500">No line items parsed for this document.</p>
      </vx-card>

      <!-- Lifecycle + note -->
      <vx-card title="Lifecycle" no-shadow card-border class="mb-5">
        <dl class="detail-list">
          <dt>Document Type</dt><dd>{{ typeLabel(order.documentType) }}</dd>
          <dt>Status</dt><dd>{{ order.status }}</dd>
          <dt>Changes Applied</dt><dd>{{ order.changeCount }}</dd>
          <dt>Currency</dt><dd>{{ order.documentCurrency || '—' }}</dd>
          <dt>Payment Terms</dt><dd>{{ order.paymentTerms || '—' }}</dd>
          <dt>Order UUID</dt><dd class="break-all">{{ order.orderUuid || '—' }}</dd>
          <dt>Customization</dt><dd class="break-all">{{ order.customizationId || '—' }}</dd>
          <dt>Profile</dt><dd class="break-all">{{ order.profileId || '—' }}</dd>
        </dl>
        <div v-if="order.note" class="mt-3">
          <p class="text-xs text-gray-500">Note</p>
          <p class="text-sm">{{ order.note }}</p>
        </div>
      </vx-card>

      <!-- Send Order Response / Order Agreement (BIS Advanced Ordering 3) -->
      <vx-card title="Respond to this Order" no-shadow card-border class="mb-5">
        <p class="text-sm text-gray-600 mb-3">
          Reply to the buyer over the Peppol network (delivered to
          <span class="font-medium">{{ order.buyerName || order.senderPid }}</span>).
          Send an <span class="font-medium">Order Response</span> to accept, amend or reject
          the order, or an <span class="font-medium">Order Agreement</span> to confirm the
          agreed order.
        </p>
        <vs-alert v-if="order.status === 'RESPONDED'" color="success" icon="check_circle" active>
          <!-- A response has already been sent for this order. -->
             You can send more than one response on the same order (e.g. acknowledge, then
            accept or reject).
        </vs-alert>
        <vs-alert v-else-if="order.isCancelled" color="danger" icon="cancel" active>
          This order was cancelled by the buyer; no response can be sent.
        </vs-alert>
        <div class="flex flex-wrap gap-3">
          <vs-button
            color="primary"
            icon-pack="feather"
            icon="icon-send"
            @click="openResponse('OrderResponse')"
          >
            Send Order Response
          </vs-button>
          <vs-button
            color="success"
            type="border"
            icon-pack="feather"
            icon="icon-check-circle"
            @click="openResponse('OrderAgreement')"
          >
            Send Order Agreement
          </vs-button>
        </div>
      </vx-card>
    </div>

    <div v-else class="text-center py-10">
      <p class="text-gray-500">Order not found.</p>
    </div>

    <!-- Convert to Invoice modal -->
    <vs-popup :active.sync="showConvertModal" :title="$t('order.convertToInvoice')">
      <div class="mb-4">
        <label class="text-sm font-medium">{{ $t('order.message.enterInvoiceDocNo') }}</label>
        <vs-input v-model="convertInvoiceDocNo" class="w-full" name="invoiceDocNo" />
      </div>
      <div class="flex justify-end">
        <vs-button
          type="border"
          color="dark"
          class="mr-2"
          @click="showConvertModal = false"
        >{{ $t('dashboard.cancel') }}</vs-button>
        <vs-button
          color="primary"
          :disabled="converting || !convertInvoiceDocNo"
          @click="convertToInvoice"
        >{{ converting ? $t('order.convert') + '…' : $t('order.convert') }}</vs-button>
      </div>
    </vs-popup>
    <!-- Response modal -->
    <vs-popup :active.sync="showResponseModal" :title="responseModalTitle">
      <div class="mb-4">
        <label class="text-sm font-medium">Response Code</label>
        <vs-select v-model="responseCode" class="w-full">
          <vs-select-item
            v-for="opt in activeResponseCodes"
            :key="opt.value"
            :value="opt.value"
            :text="opt.text"
          />
        </vs-select>
      </div>

      <!-- T116 line-level decisions: only Order Response code CA may (and must)
           carry order lines, so the per-line editor appears for CA only. -->
      <div v-if="showLineEditor" class="mb-4">
        <label class="text-sm font-medium">Line decisions</label>
        <p class="text-xs text-gray-400 mb-2">
          A conditionally accepted order must state a decision for each line.
        </p>
        <vs-table v-if="responseLines.length" :data="responseLines" stripe>
          <template slot="thead">
            <vs-th>#</vs-th>
            <vs-th>Item</vs-th>
            <vs-th>Qty</vs-th>
            <vs-th>Decision</vs-th>
          </template>
          <template slot-scope="{ data }">
            <vs-tr :key="row.lineId" v-for="row in data">
              <vs-td>{{ row.lineId }}</vs-td>
              <vs-td>{{ row.name || '—' }}</vs-td>
              <vs-td>{{ row.quantity != null ? row.quantity : '—' }}</vs-td>
              <vs-td>
                <vs-select v-model="row.statusCode" class="w-full">
                  <vs-select-item
                    v-for="opt in lineStatusOptions"
                    :key="opt.value"
                    :value="opt.value"
                    :text="opt.text"
                  />
                </vs-select>
              </vs-td>
            </vs-tr>
          </template>
        </vs-table>
        <vs-alert v-else color="warning" icon="warning" active>
          No order lines were parsed for this order, so a conditionally accepted
          response cannot be built. Choose a different response code.
        </vs-alert>
      </div>

      <div class="mb-4">
        <label class="text-sm font-medium">Note (optional)</label>
        <vs-textarea v-model="responseNote" rows="3" class="w-full" />
        <p class="text-xs text-gray-400">
          Recorded against the order for audit. The Peppol message carries the coded
          status only.
        </p>
      </div>
      <div class="flex justify-end">
        <vs-button type="border" color="dark" class="mr-2" @click="showResponseModal = false">
          Cancel
        </vs-button>
        <vs-button color="primary" :disabled="submitDisabled" @click="submitResponse">
          {{ sending ? 'Sending…' : 'Send' }}
        </vs-button>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import moment from 'moment'
import ministryBoardData from '@/assets/data/MinistryBoard.json'

export default {
  data() {
    return {
      govtOrder: false,
      selectedMinistry: null,
      selectedSubBusinessUnit: null,
      ministryBoardData,
      loading: false,
      order: null,
      showResponseModal: false,
      showConvertModal: false,
      convertInvoiceDocNo: '',
      converting: false,
      responseDocType: 'OrderResponse',
      responseCode: 'AP',
      responseNote: '',
      responseLines: [],
      sending: false,
      // PEPPOL-T116 OrderResponseCode codelist (clUNCL4343-T76): only these four
      // are valid for an Advanced Order Response. CA is the only code permitted
      // to carry order lines (R007); AP/RE/AB must NOT (R008/R009/R010).
      orderResponseCodes: [
        { value: 'AP', text: 'AP — Accepted' },
        { value: 'AB', text: 'AB — Acknowledged' },
        { value: 'CA', text: 'CA — Conditionally accepted' },
        { value: 'RE', text: 'RE — Rejected' },
      ],
      // BIS Order Agreement (T110) status codes (api-core IRSP_RESPONSE_CODES).
      orderAgreementCodes: [
        { value: 'AP', text: 'AP — Accepted' },
        { value: 'CA', text: 'CA — Conditionally accepted' },
        { value: 'AB', text: 'AB — Acknowledged' },
      ],
      // UNCL1229 line status subset permitted by T116 (clUNCL1229).
      lineStatusOptions: [
        { value: '5', text: 'Accepted (as ordered)' },
        { value: '3', text: 'Amended' },
        { value: '7', text: 'Not accepted' },
      ],
    }
  },
  computed: {
    lineItems() {
      return (this.order && Array.isArray(this.order.lineItems) && this.order.lineItems) || []
    },
    ministryOptions() {
      const values = this.ministryBoardData
        .map(item => item['MINISTRY / STATUTORY BOARD'])
        .filter(Boolean)

      return [...new Set(values)]
    },
    govtSubBusinessUnitOptions() {
      if (!this.selectedMinistry) return []

      return this.ministryBoardData
        .filter(
          item =>
            item['MINISTRY / STATUTORY BOARD'] === this.selectedMinistry
        )
        .map(item => ({
          value: item['SUB-BUSINESS UNIT'],
          text: item['SUB-BUSINESS UNIT']
        }))
        .filter(item => item.value)
    },
    activeResponseCodes() {
      return this.responseDocType === 'OrderAgreement'
        ? this.orderAgreementCodes
        : this.orderResponseCodes
    },
    responseModalTitle() {
      return this.responseDocType === 'OrderAgreement'
        ? 'Send Order Agreement'
        : 'Send Order Response'
    },
    // Per-line editor is only meaningful for an Order Response with code CA.
    showLineEditor() {
      return this.responseDocType === 'OrderResponse' && this.responseCode === 'CA'
    },
    submitDisabled() {
      if (this.sending || !this.responseCode) return true
      // CA requires at least one order line (PEPPOL-T116-R007).
      if (this.showLineEditor && this.responseLines.length === 0) return true
      return false
    },
    canConvertToInvoice() {
      if (!this.order) return false
      if (this.order.documentType === 'OrderCancellation' || this.order.status == "CANCELLED") return false
      return true
    },
  },
  created() {
    this.fetch()
  },
  watch: {
    selectedMinistry() {
      this.selectedSubBusinessUnit = null
    },

    govtOrder(value) {
      if (!value) {
        this.selectedMinistry = null
        this.selectedSubBusinessUnit = null
      }
    }
  },
  methods: {
    async fetch() {
      this.loading = true
      try {
        this.order = await this.$store.dispatch(
          'peppolInboundOrders/fetchRecord',
          this.$route.params.id,
        )
      } catch (e) {
        console.error('[InboundOrders] detail fetch failed:', e)
        this.$vs.notify({
          color: 'danger',
          title: 'Error',
          text: e.response?.data?.message || 'Failed to load order',
        })
      } finally {
        this.loading = false
      }
    },
    openResponse(docType = 'OrderResponse') {
      this.responseDocType = docType
      this.responseCode = 'AP'
      this.responseNote = ''
      // Seed per-line decisions from the parsed order lines, defaulting each to
      // "Accepted". Line ids are 1-based sequence numbers so they reference the
      // original order lines (OrderLineReference/LineID) and stay unique (R003).
      this.responseLines = this.lineItems.map((line, i) => ({
        lineId: String(i + 1),
        name: line.name || line.description || '',
        quantity: line.quantity != null ? line.quantity : null,
        statusCode: '5',
      }))
      this.showResponseModal = true
    },
    async submitResponse() {
      if (this.submitDisabled) return
      this.sending = true
      const isAgreement = this.responseDocType === 'OrderAgreement'
      try {
        const payload = {
          documentType: this.responseDocType,
          inboundId: this.order.id,
          responseCode: this.responseCode,
          note: this.responseNote || undefined,
        }
        // Attach per-line decisions only for an Order Response CA (T116).
        if (this.showLineEditor) {
          payload.lines = this.responseLines.map((row) => ({
            lineId: row.lineId,
            statusCode: row.statusCode,
          }))
        }
        await this.$store.dispatch('sgPeppolSubmissions/sendResponse', payload)
        this.$vs.notify({
          color: 'success',
          title: isAgreement ? 'Order Agreement sent' : 'Order Response sent',
          text: `${this.responseCode} delivered to ${this.order.buyerName ||
            this.order.senderPid}`,
        })
        this.showResponseModal = false
        await this.fetch()
      } catch (e) {
        this.$vs.notify({
          color: 'danger',
          title: isAgreement ? 'Failed to send agreement' : 'Failed to send response',
          text:
            e.response?.data?.message ||
            `Could not send the ${isAgreement ? 'order agreement' : 'order response'}`,
        })
      } finally {
        this.sending = false
      }
    },
    showConvertDialog() {
      this.convertInvoiceDocNo = ''
      this.showConvertModal = true
    },
    async convertToInvoice() {
      const invoiceDocNo = (this.convertInvoiceDocNo || '').trim()
      if (!invoiceDocNo) {
        this.$vs.notify({
          color: 'warning',
          title: this.$t('order.message.failedTitle'),
          text: 'Invoice Doc No is required',
        })
        return
      }
      if (this.govtOrder && !this.selectedMinistry) {
        this.$vs.notify({
          color: 'warning',
          title: this.$t('order.message.failedTitle'),
          text: 'Please select a Ministry / Statutory Board'
        })
        return
      }

      if (this.govtOrder && !this.selectedSubBusinessUnit) {
        this.$vs.notify({
          color: 'warning',
          title: this.$t('order.message.failedTitle'),
          text: 'Please select a Sub-Business Unit'
        })
        return
      }
      this.converting = true
      try {
        const payload = {
          docNo: this.order.orderId,
          invoiceDocNo,
        }

        if (this.govtOrder) {
          payload.buyer = this.selectedSubBusinessUnit
        }

        const response = await this.$store.dispatch(
          'billingDocs/convertOrderToInvoice',
          payload
        )
        if (response.data && response.data.succeeded && response.data.succeeded.length > 0) {
          const newInvoice = response.data.succeeded[0]
          this.showConvertModal = false
          this.$vs.notify({
            color: 'success',
            title: this.$t('order.message.convertSuccess'),
            text: `Invoice ${newInvoice.docNo} created successfully`,
          })
          this.$router
            .push('/invoices/' + encodeURIComponent(newInvoice.docNo))
            .catch(() => {})
        }
      } catch (err) {
        this.$vs.notify({
          color: 'danger',
          title: this.$t('order.message.failedTitle'),
          text: err?.response?.data?.message || 'Failed to convert order to invoice',
        })
      } finally {
        this.converting = false
      }
    },
    typeLabel(type) {
      return (
        {
          Order: 'Order',
          OrderChange: 'Order Change',
          OrderCancellation: 'Order Cancellation',
          OrderBalance: 'Order Balance',
        }[type] || type
      )
    },
    statusColor(status) {
      return (
        {
          RECEIVED: 'primary',
          CHANGED: 'warning',
          CANCELLED: 'danger',
          RESPONDED: 'success',
          CONVERTED: 'success',
        }[status] || 'primary'
      )
    },
    formatAmount(amount) {
      if (amount == null || isNaN(amount)) return '—'
      const value = Number(amount).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
      return this.order && this.order.documentCurrency
        ? `${this.order.documentCurrency} ${value}`
        : value
    },
    formatDate(d) {
      return moment(d).format('DD MMM YYYY')
    },
    formatDateTime(d) {
      return moment(d).format('DD MMM YYYY HH:mm')
    },
  },
}
</script>

<style lang="scss" scoped>
.detail-list {
  display: grid;
  grid-template-columns: 140px 1fr;
  row-gap: 0.5rem;
  column-gap: 1rem;

  dt {
    color: #6b7280;
    font-size: 0.8rem;
  }
  dd {
    font-size: 0.9rem;
    margin: 0;
  }
}
</style>
